import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), href: '/', hash: '' },
    { name: t('nav.howItWorks'), href: '/', hash: '#how-it-works' },
    { name: t('nav.solutions'), href: '/', hash: '#solutions' },
    { name: t('nav.partners'), href: '/', hash: '#partners' },
    { name: t('nav.popupPhoto'), href: '/popup-photo', hash: '' },
    { name: t('nav.contact'), href: '/', hash: '#contact' },
  ];

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.hash && location.pathname === '/') {
      const element = document.querySelector(link.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#050505]/90 backdrop-blur-md border-b border-white/5' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <div className="relative">
                <div className="absolute inset-0 bg-[#FF7A00]/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img 
                  src="https://customer-assets.emergentagent.com/job_ifotolife-popup/artifacts/aoncqacu_ChatGPT%20Image%2028%20janv.%202026%2C%2014_08_19.png" 
                  alt="iFotoLife" 
                  className="h-14 w-auto relative z-10 drop-shadow-[0_0_10px_rgba(255,122,0,0.3)]"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href + link.hash}
                  onClick={() => handleNavClick(link)}
                  data-testid={`nav-link-${index}`}
                  className="text-white/70 hover:text-[#FF7A00] transition-colors duration-300 text-sm font-medium"
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10 hover:border-[#FF7A00]/50 transition-all text-sm"
              >
                <Globe size={16} className="text-[#FF7A00]" />
                <span className="text-white font-medium">{language.toUpperCase()}</span>
              </button>

              {/* CTA Button */}
              <Link
                to="/#contact"
                data-testid="nav-demo-btn"
                className="btn-primary text-sm px-6 py-3"
                onClick={() => {
                  if (location.pathname === '/') {
                    const element = document.querySelector('#contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {t('nav.demo')}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                data-testid="mobile-language-toggle"
                className="p-2 rounded-full bg-white/5 border border-white/10"
              >
                <Globe size={18} className="text-[#FF7A00]" />
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-btn"
                className="p-2 text-white"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-[#050505]/95 backdrop-blur-lg" />
            <div className="relative pt-24 px-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.href + link.hash}
                    onClick={() => handleNavClick(link)}
                    data-testid={`mobile-nav-link-${index}`}
                    className="text-white text-xl font-medium py-3 border-b border-white/10 hover:text-[#FF7A00] transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/#contact"
                  data-testid="mobile-demo-btn"
                  className="btn-primary text-center mt-4"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    if (location.pathname === '/') {
                      setTimeout(() => {
                        const element = document.querySelector('#contact');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                >
                  {t('nav.demo')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
